import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';

import messages from './messages';
import styles from './style.module.less';
import UserDateTimezoneFormat from '../../../../../components/user-date-timezone-format';
import { TRIAL_EXPIRED, PENDING_CANCELLATION, PAYMENT_ISSUE, CANCELLED, NONE } from '../../constants/account-issues';
import { useTrackEvent, PageEvents, PageEventSourceStrings } from '../../hooks/use-track-event';
import { messages as commonMessages } from '../../messages';
import { ChangeSubscriptionButtonWrapper } from '../change-subscription-button-wrapper';

const getStateFromStatus = (status) => {
  if (status === CANCELLED || status === PENDING_CANCELLATION) {
    return 'warning';
  }
  return 'error';
};

export const AccountIssueBanner = ({
  billingCycleEndDateTimeUTC = '',
  cancelledDateTimeUTC = '',
  isPlanLocked = false,
  reactivatePlan,
  status,
}) => {
  const trackSubscriptionChange = useTrackEvent(PageEvents.SUBSCRIPTION_CHANGE_START, PageEventSourceStrings.ACCOUNT_ISSUES_BANNER);
  const trackReactivatePlan = useTrackEvent(PageEvents.UNDO_SUBSCRIPTION_CANCELLATION, PageEventSourceStrings.ACCOUNT_ISSUES_BANNER);
  const onReactivatePlanClick = useCallback(() => {
    reactivatePlan();
    trackReactivatePlan();
  }, [reactivatePlan, trackReactivatePlan]);

  // PAYMENT_ISSUE is now handled in a different banner -- past-due-banner.js
  if (status === NONE || status === PAYMENT_ISSUE || isPlanLocked) {
    return null;
  }

  const bannerTitles = {
    [TRIAL_EXPIRED]: <FormattedMessage {...messages.trialExpiredTitle} />,
    [PENDING_CANCELLATION]: (
      <FormattedMessage
        {...messages.pendingAccountCancel}
        values={{
          endDate: <UserDateTimezoneFormat dateTime={billingCycleEndDateTimeUTC} />,
        }}
      />
    ),
    [CANCELLED]: (
      <FormattedMessage
        {...messages.accountCancelledTitle}
        values={{
          endDate: <UserDateTimezoneFormat dateTime={cancelledDateTimeUTC} />,
        }}
      />
    ),
  };

  const bannerMessages = {
    [TRIAL_EXPIRED]: <FormattedMessage {...messages.trialExpiredMessage} />,
    [PENDING_CANCELLATION]: (
      <FormattedMessage
        {...messages.pendingAccountCancelMessage}
        values={{
          endDate: <UserDateTimezoneFormat dateTime={billingCycleEndDateTimeUTC} />,
        }}
      />
    ),
    [CANCELLED]: (
      <FormattedMessage
        {...messages.accountCancelledMessage}
        values={{
          endDate: <UserDateTimezoneFormat dateTime={cancelledDateTimeUTC} />,
        }}
      />
    ),
  };

  const bannerLinks = {
    [CANCELLED]: (
      <ChangeSubscriptionButtonWrapper onClickTrack={trackSubscriptionChange} theme='tertiary'>
        <FormattedMessage {...commonMessages.renewPlan} />
      </ChangeSubscriptionButtonWrapper>
    ),
    [PENDING_CANCELLATION]: (
      <Button onClick={onReactivatePlanClick} theme='tertiary'>
        <FormattedMessage {...messages.undoCancellation} />
      </Button>
    ),
    [TRIAL_EXPIRED]: (
      <ChangeSubscriptionButtonWrapper onClickTrack={trackSubscriptionChange} theme='tertiary'>
        <FormattedMessage {...commonMessages.chooseSubscription} />
      </ChangeSubscriptionButtonWrapper>
    ),
  };

  const bannerTitle = bannerTitles[status];
  const bannerMessage = bannerMessages[status];
  const bannerLink = bannerLinks[status];

  return (
    <BannerMessageV2
      className={styles['info-banner-expired']}
      content={
        <>
          {bannerTitle}
          <div className={styles['banner-message']}>{bannerMessage}</div>
          <div className={styles['banner-link']}>{bannerLink}</div>
        </>
      }
      status={getStateFromStatus(status) === 'warning' ? BannerStatus.Warning : BannerStatus.Error}
    />
  );
};

AccountIssueBanner.propTypes = {
  billingCycleEndDateTimeUTC: PropTypes.string,
  cancelledDateTimeUTC: PropTypes.string,
  isPlanLocked: PropTypes.bool,
  reactivatePlan: PropTypes.func.isRequired,
  status: PropTypes.oneOf([NONE, PENDING_CANCELLATION, CANCELLED, PAYMENT_ISSUE, TRIAL_EXPIRED]).isRequired,
};
